import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Brain,
  ClipboardCheck,
  DollarSign,
  CreditCard,
  Users,
  Settings,
  Menu,
  X,
  LogOut,
  Shield,
  BookOpen,
  Clock,
  History,
  LayoutDashboard,
  BarChart3
} from 'lucide-react';
import { ROUTES, USER_ROLES, hasPermission } from '../../constants';
import { ThemeToggle } from '../ui/ThemeToggle';
import { RoleViewToggle } from '../ui/RoleViewToggle';
import { useAuth } from '../../../contexts/AuthProvider';
import { useRoleView } from '../../../contexts/RoleViewContext';
import LogoA from '../../../assets/A.svg';
import { useReviewQueue } from '../../../pages/review/modules/hooks/useReviewQueue';

interface SidebarItem {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  badge?: string | number;
  disabled?: boolean;
  premiumBadge?: boolean;
}

interface SidebarSection {
  title?: string;
  items: SidebarItem[];
}

interface SidebarProps {
  currentPath?: string;
  isOpen?: boolean;
  onToggle?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  currentPath = ROUTES.QUESTIONS_MANAGEMENT,
  onToggle
}) => {
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { effectiveRole } = useRoleView();

  // Get review queue stats for the badge
  const { stats } = useReviewQueue('pending');


  // Build sidebar config based on permissions using effective role
  const sidebarConfig: SidebarSection[] = [];

  // Main section
  const mainItems: SidebarItem[] = [];

  // Dashboard (all authenticated users)
  mainItems.push({ label: "Dashboard", icon: LayoutDashboard, href: ROUTES.DASHBOARD });

  // Analytics (show for all users, but disabled for free users)
  const isPremium = user?.subscription_status === 'active' || user?.subscription_status === 'grandfathered' || user?.is_grandfathered === true || user?.role === USER_ROLES.ADMIN;
  mainItems.push({
    label: "Analytics",
    icon: BarChart3,
    href: ROUTES.ANALYTICS,
    disabled: !isPremium,
    premiumBadge: !isPremium
  });

  // Billing (all authenticated users)
  mainItems.push({ label: "Billing", icon: CreditCard, href: "/billing" });

  // Always show Question Demo for all authenticated users
  if (hasPermission(effectiveRole || undefined, 'VIEW_QUESTION_DEMO')) {
    mainItems.push({ label: "Question Demo", icon: BookOpen, href: ROUTES.QUESTION_DEMO });
  }

  // Exams (all authenticated users)
  if (hasPermission(effectiveRole || undefined, 'VIEW_EXAM')) {
    mainItems.push({ label: "Exams", icon: BookOpen, href: ROUTES.EXAM });
  }

  // Exam History (all authenticated users)
  if (hasPermission(effectiveRole || undefined, 'VIEW_EXAM_HISTORY')) {
    mainItems.push({ label: "Exam History", icon: History, href: ROUTES.EXAM_HISTORY });
  }

  // Questions Management (Admin only)
  if (hasPermission(effectiveRole || undefined, 'VIEW_QUESTIONS_MANAGEMENT')) {
    mainItems.push({ label: "Questions Management", icon: Brain, href: ROUTES.QUESTIONS_MANAGEMENT });
  }

  // Review Queue (Reviewer, Senior Reviewer, Admin)
  if (hasPermission(effectiveRole || undefined, 'VIEW_REVIEW_QUEUE')) {
    mainItems.push({
      label: "Review Queue",
      icon: ClipboardCheck,
      href: ROUTES.REVIEW,
      badge: stats.pending > 0 ? stats.pending : undefined
    });
  }

  if (mainItems.length > 0) {
    sidebarConfig.push({ items: mainItems });
  }

  // Management section
  const managementItems: SidebarItem[] = [];

  // Financial (Admin only)
  if (hasPermission(effectiveRole || undefined, 'VIEW_FINANCIAL')) {
    managementItems.push({ label: "Financial", icon: DollarSign, href: ROUTES.FINANCIAL });
  }

  // Budget (Admin only)
  if (hasPermission(effectiveRole || undefined, 'VIEW_BUDGET')) {
    managementItems.push({ label: "Budget", icon: DollarSign, href: ROUTES.BUDGET });
  }

  // Users (Senior Reviewer, Admin)
  if (hasPermission(effectiveRole || undefined, 'VIEW_USERS')) {
    managementItems.push({ label: "Users", icon: Users, href: ROUTES.USERS });
  }

  // Admin (Admin only)
  if (hasPermission(effectiveRole || undefined, 'VIEW_ADMIN')) {
    managementItems.push({ label: "Admin", icon: Shield, href: ROUTES.ADMIN });
  }

  if (managementItems.length > 0) {
    sidebarConfig.push({
      title: "Management" as const,
      items: managementItems
    });
  }


  const toggleMobile = () => {
    setIsMobileOpen(!isMobileOpen);
    onToggle?.();
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth/login');
    } catch (error) {
      // Logout failed
    }
  };

  return (
    <>
      {/* Mobile Menu Button - Only show on mobile/tablet */}
      <button
        onClick={toggleMobile}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-sm bg-default-background dark:bg-neutral-900 border border-neutral-border dark:border-neutral-800 shadow-sm"
      >
        {isMobileOpen ? (
          <X className="h-5 w-5 text-default-font dark:text-neutral-100" />
        ) : (
          <Menu className="h-5 w-5 text-default-font dark:text-neutral-100" />
        )}
      </button>

      {/* Mobile Backdrop - Only show on mobile/tablet */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-neutral-900 bg-opacity-50 dark:bg-opacity-70 z-40"
          onClick={toggleMobile}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        w-60 bg-default-background dark:bg-neutral-900 border-r border-neutral-border dark:border-neutral-800 min-h-screen
        flex flex-col fixed lg:static inset-y-0 left-0 z-50
        transform transition-transform duration-300 ease-in-out
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Sidebar Header */}
        <div className="p-6 border-b border-neutral-border dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <img
              src={LogoA}
              alt="Qbank by Aksanio"
              className="h-8 w-8"
              style={{ filter: 'brightness(0) saturate(100%) invert(41%) sepia(84%) saturate(1144%) hue-rotate(195deg) brightness(91%) contrast(90%)' }}
            />
            <div>
              <h1 className="text-heading-3 font-heading-3 text-default-font dark:text-neutral-100">
                Qbank
              </h1>
              <p className="text-caption text-subtext-color dark:text-neutral-400">
                By Aksanio
              </p>
            </div>
          </div>
        </div>

        {/* Sidebar Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {sidebarConfig.map((section, sectionIndex) => (
            <div key={`section-${sectionIndex}-${section.title || 'main'}`} className="mb-8">
              {section.title && (
                <h2 className="px-3 py-2 text-caption-bold font-caption-bold text-subtext-color dark:text-neutral-500 uppercase tracking-wider mb-2">
                  {section.title}
                </h2>
              )}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentPath === item.href;
                  const isDisabled = item.disabled;

                  const linkContent = (
                    <div
                      className={`
                        flex items-center gap-3 px-3 py-2 rounded-sm text-body font-body
                        transition-all duration-200
                        ${isDisabled
                          ? 'cursor-not-allowed opacity-50'
                          : 'cursor-pointer'
                        }
                        ${isActive && !isDisabled
                          ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-600 dark:text-brand-400'
                          : isDisabled
                            ? 'text-subtext-color dark:text-neutral-500'
                            : 'text-subtext-color dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 hover:text-default-font dark:hover:text-neutral-100'
                        }
                      `}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      {item.premiumBadge && (
                        <span className="text-caption-bold font-caption-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-brand-500 to-brand-600 text-white">
                          Premium
                        </span>
                      )}
                      {item.badge && !item.premiumBadge && (
                        <span className={`
                          text-caption font-caption-bold px-2 py-0.5 rounded-full
                          ${isActive
                            ? 'bg-brand-600 dark:bg-brand-500 text-white'
                            : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
                          }
                        `}>
                          {item.badge}
                        </span>
                      )}
                    </div>
                  );

                  return isDisabled ? (
                    <div key={item.href}>
                      {linkContent}
                    </div>
                  ) : (
                    <Link key={item.href} to={item.href}>
                      {linkContent}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-neutral-border dark:border-neutral-800 space-y-3">
          {/* Role View Toggle (Admin Only) */}
          <div className="px-3">
            <RoleViewToggle />
          </div>

          {/* Theme Toggle */}
          <div className="flex items-center justify-between px-3">
            <span className="text-body text-default-font dark:text-neutral-100">Theme</span>
            <ThemeToggle />
          </div>

          {/* User Profile */}
          <div className="space-y-2">
            <div className="px-3 py-2">
              <div>
                <p className="text-body font-body-bold text-default-font dark:text-neutral-100">
                  {(() => {
                    if (user?.firstName || user?.lastName) {
                      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
                    } else if (user?.email) {
                      return user.email;
                    }
                    return 'User';
                  })()}
                </p>
                {(user?.role === USER_ROLES.REVIEWER || user?.role === USER_ROLES.SENIOR_REVIEWER || user?.role === USER_ROLES.ADMIN) && (
                  <p className="text-caption text-subtext-color dark:text-neutral-400">
                    {user.role.replace('_', ' ')}
                  </p>
                )}
              </div>
            </div>
            
            {/* Logout Button */}
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-sm text-body font-body
                text-subtext-color dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 
                hover:text-error-600 dark:hover:text-error-400 transition-all duration-200"
            >
              <LogOut className="h-5 w-5 flex-shrink-0" />
              <span className="flex-1 text-left">Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};